<template>
  <ProvideApiFilebank v-slot="{ deleteFiles: fileDeleter, deleting }">
    <WdsModal
      no-close-icon
      lg
      :show="isOpen"
      class="modal-timesheet"
      :disable-click-outside="true"
      @cancel="attemptToClose"
    >
      <div class="tw-flex tw-flex-col tw-gap-sm tw-mb-6">
        <WdsAlert success icon :hidden="!showParserAlert">
          <div class="tw-flex tw-justify-between tw-flex-row tw-gap-sm">
            <span class="tw-flex tw-items-center tw-gap-sm">
              {{
                $tc(
                  "We've parsed :count entry from the file. Please verify the data. | We've parsed :count entries from the files. Please verify the data.",
                  timesheetParsingStore.countOfValidParsedRegistrationsToBeFilled,
                  {
                    count: timesheetParsingStore.countOfValidParsedRegistrationsToBeFilled,
                  },
                )
              }}
            </span>
            <WdsButtonText
              :disabled="deleting"
              @click="clearParsedTimesheetRegistrations(fileDeleter)"
            >
              {{ $t('Discard all') }}
            </WdsButtonText>
          </div>
        </WdsAlert>
        <WdsAlert
          warning
          icon
          :hidden="!showParserAlertForParsedEntriesOutsideBillingPeriod"
          :closable="true"
          @close="timesheetParsingStore.userClosedAlertForParsedEntriesOutsideBillingPeriod = true"
        >
          <div class="tw-flex tw-justify-between tw-flex-row tw-gap-sm">
            {{
              $tc(
                ':count entry found was outside of the billing period and was ignored. | :count entries found were outside of the billing period and were ignored.',
                timesheetParsingStore.countOfRegistrationsOutsideOfTheCurrentPeriod,
                {
                  count: timesheetParsingStore.countOfRegistrationsOutsideOfTheCurrentPeriod,
                },
              )
            }}
          </div>
        </WdsAlert>
        <WdsAlert
          warning
          icon
          :hidden="timesheetParsingStore.countOfRegistrationsWithIncompatibleRateType === 0"
        >
          <div class="tw-flex tw-justify-between tw-flex-row tw-gap-sm">
            {{
              $tc(
                ':count entry found was of incompatible rate type for this contract and was ignored. | :count entries found were of incompatible rate type for this contract and were ignored.',
                timesheetParsingStore.countOfRegistrationsWithIncompatibleRateType,
                {
                  count: timesheetParsingStore.countOfRegistrationsWithIncompatibleRateType,
                },
              )
            }}
          </div>
        </WdsAlert>
      </div>
      <PageHeader>
        <template #header-actions>
          <div v-if="props.enableAiTimesheetParsing">
            <Parser :open-file-picker-on-mount="props.openFilePickerOnMount" />
          </div>
        </template>
        <template v-if="!timesheetFormStore.isViewOnly" #header-breadcrumbs>
          <WdsButtonGhost secondary @click="attemptToClose">
            <WdsStack gap-sm>
              <WdsIcon :size="16" :name="WdsIconNameEnum.UNDO" />
              {{ $t('Payment request') }}
            </WdsStack>
          </WdsButtonGhost>
        </template>
        <template #header-title>
          {{ $t('Timesheet') }}
        </template>
        <template v-if="!isLoadingTimesheet" #header-subheading>
          <div
            class="tw-truncate tw-max-w-full"
            :title="`${timesheetFormStore.companyName} / ${timesheetFormStore.jobName}`"
          >
            {{ timesheetFormStore.companyName }} / {{ timesheetFormStore.jobName }}
          </div>
        </template>
        <template v-if="!isLoadingTimesheet" #header-metadata>
          <span>
            {{ $t('Billing period') }}:
            <span v-if="timesheetFormStore.dateStart">{{ timesheetFormStore.dateStart }} /</span>
            {{ timesheetFormStore.dateEnd }}
          </span>
        </template>
        <template v-if="!isLoadingTimesheet" #header-metadata-right>
          <WdsText small>
            {{ $t('Need help?') }}
            <a href="#" class="intercomchat">{{ $t('Chat with us') }}.</a>
          </WdsText>
        </template>
      </PageHeader>

      <Timesheet :loading="isLoadingTimesheet" />

      <template #footer>
        <WdsDivider class="tw-mb-md" />
        <div
          v-if="!timesheetFormStore.isViewOnly"
          class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center tw-gap-4"
        >
          <div class="tw-flex tw-gap-md tw-items-center">
            <template v-if="enableTimesheetLines">
              <WdsButtonGhost
                class="tw-flex tw-items-center tw-gap-sm tw-text-nowrap"
                data-test="add-timesheet-line-button"
                @click="timesheetLinesStore.openModalToAddNewLine"
              >
                <WdsIcon :size="10" name="add" />
                {{ $t('Add new line') }}
              </WdsButtonGhost>
              <div
                class="tw-border-solid tw-border-[1px] tw-border-l-0 tw-border-gray-300 tw-h-5"
              />
            </template>
            <StatsOverview />
          </div>
          <div
            class="tw-flex tw-flex-nowrap tw-items-center tw-gap-md tw-w-full sm:tw-w-auto tw-justify-end"
          >
            <WdsButtonGhost
              :disabled="isWorking || deleting"
              error
              data-test="timesheet-clear-all-entries-button"
              class="tw-whitespace-nowrap"
              @click="rollbackAllTimesheetRegistrations(fileDeleter)"
            >
              <div class="tw-flex tw-items-center tw-gap-xs">
                <WdsIcon :size="16" name="trash-can" />
                {{ $t('Clear all') }}
              </div>
            </WdsButtonGhost>

            <WdsButton
              :disabled="isWorking"
              default
              data-test="timesheet-cancel-button"
              @click="attemptToClose"
            >
              {{ $t('Cancel') }}
            </WdsButton>
            <WdsButton
              :disabled="isWorking"
              primary
              data-test="timesheet-save-button"
              @click="saveTimesheet"
            >
              {{ isCreatingTimesheet || isUpdatingTimesheet ? $t('Saving...') : $t('Save') }}
            </WdsButton>
          </div>
        </div>
        <div v-else class="tw-flex tw-justify-end">
          <WdsButton default @click="close">
            {{ $t('Close') }}
          </WdsButton>
        </div>
        <!--      <div>-->
        <WdsAlert
          warning
          icon
          :hidden="!displayValidationMessage"
          class="tw-mt-4"
          data-test="timesheet-validation-message"
        >
          {{ validationMessage }}
        </WdsAlert>
      </template>
    </WdsModal>

    <ModalTimesheetConfirmation
      :show="showCancelModal"
      :title="$t('Cancel creating timesheet?')"
      :text="$t(`You'll need to create this timesheet again if you exit`)"
      :primary-button-text="$t('Cancel')"
      :secondary-button-text="$t('Back to editor')"
      @primary-button-click="handleConfirmCancel(fileDeleter)"
      @secondary-button-click="handleConfirmDontCancel"
    />
    <ModalTimesheetConfirmation
      :show="showSaveModal"
      :title="$t('Save changes?')"
      :text="$t('There are unsaved changes to this timesheet')"
      :primary-button-text="$t('Save and close')"
      :secondary-button-text="$t(`Don't save`)"
      @primary-button-click="handleConfirmSave"
      @secondary-button-click="handleConfirmDontSave(fileDeleter)"
    />
    <Teleport to="body">
      <ModalAddEditLine
        v-if="enableTimesheetLines"
        :title="timesheetLinesStore.titleForModalAddEditLine"
        :description="timesheetLinesStore.descriptionForModalAddEditLine"
        :show="timesheetLinesStore.isOpenModalToAddOrEditLine"
        :primary-button-text="$t('Add')"
        :secondary-button-text="$t('Cancel')"
        @cancel="timesheetLinesStore.closeModalToAddOrEditLine"
      />
    </Teleport>
  </ProvideApiFilebank>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import {
  WdsModal,
  WdsButton,
  WdsDivider,
  WdsButtonGhost,
  WdsStack,
  WdsIcon,
  WdsIconNameEnum,
  WdsAlert,
  WdsText,
  WdsButtonText,
} from '@worksome/wds-components'
import { useTranslation } from '@/composables/translation'
import Timesheet from '@/components/Timesheet/Timesheet.vue'
import StatsOverview from '@/components/Timesheet/StatsOverview.vue'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import ModalTimesheetConfirmation from '@/components/Timesheet/ModalTimesheetConfirmation.vue'
import { useQueryTimesheet } from '@/components/Timesheet/composables/useQueryTimesheet'
import { RateType } from '@/enums'
import { useCreateTimesheet } from '@/components/Timesheet/composables/useCreateTimesheet'
import {
  CustomFieldTypeValueInput,
  Timesheet as TimesheetGraphql,
  TimesheetRegistrationType,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { useFormattedTimesheetRegistrationsStore } from '@/components/Timesheet/store/useFormattedTimesheetRegistrationsStore'
import { useUpdateTimesheet } from '@/components/Timesheet/composables/useUpdateTimesheet'
import { useValidationStore } from '@/components/Timesheet/store/useValidationStore'
import PageHeader from '@/components/PageHeader.vue'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import ModalAddEditLine from '@/components/Timesheet/ModalAddEditLine.vue'
import Parser from '@/components/Timesheet/Parser.vue'
import { useTimesheetParsingStore } from '@/components/Timesheet/store/useTimesheetParsingStore'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'
import useFieldableCustomFields from '@/components/Fieldable/composables/useFieldableCustomFields'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import { useFragment } from '@/gql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import { customFieldValueInputFieldId, hasFieldValue } from '@/components/Fieldable/utils'

interface Props {
  isOpen: boolean
  timesheetId?: string | null
  readonly?: boolean
  startDate?: string
  endDate?: string
  jobName?: string
  companyName?: string
  rateType: RateType
  detailedView?: boolean
  issuedAt?: string
  workerId?: string
  hireId?: string
  autoLoadTimesheetData?: boolean
  enableTimesheetLines?: boolean
  hasRequiredTimesheetLineCustomFields?: boolean
  openFilePickerOnMount?: boolean
  enableAiTimesheetParsing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  readonly: false,
  rateType: RateType.HOURLY,
  startDate: '',
  endDate: '',
  jobName: '',
  companyName: '',
  detailedView: false,
  issuedAt: '',
  timesheetId: null,
  workerId: '',
  hireId: '',
  autoLoadTimesheetData: false,
  enableTimesheetLines: false,
  hasRequiredTimesheetLineCustomFields: false,
  openFilePickerOnMount: false,
  enableAiTimesheetParsing: false,
})

const { $t, $tc } = useTranslation()
const timesheetFormStore = useTimesheetFormStore()
const timesheetParsingStore = useTimesheetParsingStore()
const timesheetLinesStore = useTimesheetLinesStore()
const validationStore = useValidationStore()
const fieldableFormStore = useFieldableFormStore()
const paymentRequestStore = usePaymentRequestStore()

watchEffect(() => {
  timesheetFormStore.externallyImposedStartDate = props.startDate || props.endDate
  timesheetFormStore.externallyImposedEndDate = props.endDate || props.startDate
  timesheetFormStore.externallyImposedJobName = props.jobName
  timesheetFormStore.externallyImposedCompanyName = props.companyName
  timesheetFormStore.isViewOnly = props.readonly
  timesheetFormStore.rateType = props.rateType
  timesheetFormStore.detailedView = props.detailedView
  timesheetFormStore.issuedAt = props.issuedAt
})

// When we externally remove a timesheet id, we need to reset the store
watch(
  () => props.timesheetId,
  (updated, previous) => {
    if (!updated && previous) {
      timesheetFormStore.clearTimesheet()
    }
  },
)

// Type the emits
const emit = defineEmits<{
  (event: 'cancel'): void
  (event: 'updated-timesheet', id: string): void
}>()
//  @TODO we need to track if the state has changed of file attachments via timesheet Parsing
// if they are cancelling, we need to ditch the recently added files ? or save needs to just keep them in state or something like that
// hard to think through right now in a hurry

const close = () => {
  timesheetLinesStore.pruneLines()
  emit('cancel')
}

const timesheetIdForQuery = computed(() => {
  // In cases where we don't want to eagerly fetch data from the api,
  // we only want to fetch data when the modal is open
  // i.e. when used outside of a payment request
  if (!props.autoLoadTimesheetData) {
    return props.isOpen ? props.timesheetId || null : null
  }
  return props.timesheetId || null
})
const {
  onResult: onTimesheetLoaded,
  loading: isLoadingTimesheet,
  refetch: refetchTimesheetData,
} = useQueryTimesheet(timesheetIdForQuery)

watchEffect(() => {
  timesheetFormStore.isLoadingTimesheet = isLoadingTimesheet.value
})

onTimesheetLoaded((result) => {
  timesheetFormStore.hydrateFromTimesheetData(result?.data?.timesheet as TimesheetGraphql)
})

const validationMessage = ref($t('Please fill out all required fields'))
const displayValidationMessage = ref(false)
const openValidationMessage = (message: string) => {
  validationMessage.value = message
  displayValidationMessage.value = true
}
const closeValidationMessage = () => {
  displayValidationMessage.value = false
}

const formattedTimesheetRegistrationsStore = useFormattedTimesheetRegistrationsStore()

const shouldLoadTimesheetLineCustomFields = computed(
  () =>
    props.enableTimesheetLines &&
    props.hasRequiredTimesheetLineCustomFields &&
    paymentRequestStore.companyId !== '',
)
const timesheetLineCustomFieldAccounts = computed(() =>
  paymentRequestStore.customFieldProviderAccountIds.length
    ? paymentRequestStore.customFieldProviderAccountIds
    : [paymentRequestStore.companyId],
)
const { fields: timesheetLineCustomFields, loading: isLoadingTimesheetLineCustomFields } =
  useFieldableCustomFields(
    computed(() => paymentRequestStore.companyId),
    timesheetLineCustomFieldAccounts,
    [TypeSupportingCustomFieldValues.PaymentRequest],
    PER_PAGE_MAXIMUM,
    true,
    shouldLoadTimesheetLineCustomFields,
  )
// Also reloaded on every open: the field store is shared page-wide, so another form
// (e.g. the expense modal) may have replaced the line fields since this one loaded them.
watch(
  [timesheetLineCustomFields, () => props.isOpen],
  ([newFields]) => {
    if (!shouldLoadTimesheetLineCustomFields.value) {
      return
    }

    fieldableFormStore.updateFields(
      newFields.map((field) => useFragment(FieldableFormCustomFieldFragment, field)),
    )
  },
  { immediate: true },
)

const requiredTimesheetLineCustomFields = computed(() =>
  fieldableFormStore.fields.filter((field) => isCustomFieldRequired(field.settings)),
)

const hasCustomFieldValue = (
  customFieldValue: CustomFieldTypeValueInput,
  fieldId: string,
): boolean =>
  customFieldValueInputFieldId(customFieldValue) === fieldId && hasFieldValue(customFieldValue)

const missingRequiredTimesheetLineCustomFieldNames = computed(() =>
  requiredTimesheetLineCustomFields.value
    .filter((field) =>
      formattedTimesheetRegistrationsStore.registrations
        .filter((registration) => registration.type === TimesheetRegistrationType.Line)
        .every(
          (registration) =>
            !(registration.customFieldValues ?? []).some((customFieldValue) =>
              hasCustomFieldValue(customFieldValue, field.id),
            ),
        ),
    )
    .map((field) => field.title),
)

const formatFieldNames = (fieldNames: string[]): string => {
  if (fieldNames.length <= 2) {
    return fieldNames.join(' and ')
  }

  return `${fieldNames.slice(0, -1).join(', ')} and ${fieldNames[fieldNames.length - 1]}`
}

const requiredTimesheetLineCustomFieldsMessage = computed(() => {
  const fieldNames = missingRequiredTimesheetLineCustomFieldNames.value

  if (fieldNames.length === 0) {
    return ''
  }

  if (fieldNames.length === 1) {
    return $t('Add a timesheet line with the required detail: :field.', {
      field: fieldNames[0],
    })
  }

  return $t('Add one or more timesheet lines with the required details: :fields.', {
    fields: formatFieldNames(fieldNames),
  })
})

const timesheetPayload = computed(() => {
  const payload = {
    workerId: props.workerId,
    hireId: props.hireId,
    startDate: timesheetFormStore.dateStart || timesheetFormStore.dateEnd,
    endDate: timesheetFormStore.dateEnd,
    registrations: formattedTimesheetRegistrationsStore.registrations,
  }
  if (props.timesheetId) {
    // @ts-ignore
    payload.id = props.timesheetId
  }

  return payload
})

const {
  mutate: createTimesheetMutation,
  onDone: timesheetCreated,
  onError: timesheetCreateError,
  loading: isCreatingTimesheet,
} = useCreateTimesheet(timesheetPayload)

const {
  mutate: updateTimesheetMutation,
  onDone: timesheetUpdated,
  onError: timesheetUpdateError,
  loading: isUpdatingTimesheet,
} = useUpdateTimesheet(timesheetPayload)

const isWorking = computed(
  () =>
    isCreatingTimesheet.value ||
    isUpdatingTimesheet.value ||
    isLoadingTimesheet.value ||
    isLoadingTimesheetLineCustomFields.value,
)

const getTimesheetLineValidationMessage = (error: any): string | undefined =>
  error?.graphQLErrors?.[0]?.extensions?.validation?.['input.registrations']?.[0]

const showErrorMessageFromApi = (error: any) => {
  const timesheetLineValidationMessage = getTimesheetLineValidationMessage(error)

  if (timesheetLineValidationMessage) {
    openValidationMessage(timesheetLineValidationMessage)
    return
  }

  openValidationMessage($t('Something went wrong. Please try again.'))
}
timesheetCreateError((error) => showErrorMessageFromApi(error))
timesheetUpdateError((error) => showErrorMessageFromApi(error))

const saveTimesheet = async () => {
  if (timesheetFormStore.totalPayableTime === 0) {
    openValidationMessage($t('Cannot save timesheet with no billable time.'))
    return
  }
  const result = await validationStore.v$.$validate()
  if (!result) {
    // If we have an error message from a specific week on the timesheet, we shift the view to that week
    if (validationStore.v$.$errors?.[0]?.$propertyPath?.slice(0, 10)) {
      timesheetFormStore.jumpToWeekContainingDate(
        validationStore.v$.$errors[0].$propertyPath.slice(0, 10),
      )
    }
    openValidationMessage($t('There is an issue with the data you have entered.'))
    return
  }
  if (
    props.enableTimesheetLines &&
    props.hasRequiredTimesheetLineCustomFields &&
    requiredTimesheetLineCustomFieldsMessage.value
  ) {
    openValidationMessage(requiredTimesheetLineCustomFieldsMessage.value)
    return
  }
  closeValidationMessage()
  if (timesheetFormStore.hasPersistedTimesheet) {
    await updateTimesheetMutation()
    timesheetLinesStore.pruneLines()
    return
  }
  await createTimesheetMutation()
  timesheetLinesStore.pruneLines()
}

timesheetCreated((result) => {
  timesheetFormStore.updatePersistentTimesheetRegistrations(
    // @ts-ignore
    result.data?.createTimesheet?.registrations?.data || [],
  )
  refetchTimesheetData()
  // @ts-ignore
  emit('updated-timesheet', result.data?.createTimesheet?.id)
})
timesheetUpdated((result) => {
  timesheetFormStore.updatePersistentTimesheetRegistrations(
    // @ts-ignore
    result.data?.updateTimesheet?.registrations?.data || [],
  )
  // @ts-ignore
  emit('updated-timesheet', result.data?.updateTimesheet?.id)
})
// Exiting an updated timesheet should show warning allowing to save
const showSaveModal = ref(false)
const handleConfirmSave = async (fileDeleter: (ids: string[]) => Promise<any>) => {
  // Each parsing experience should be unique, so we reset the uploaded files
  // to keep them from appearing next time we open this timesheet
  timesheetParsingStore.uploadedFiles = []
  if (!timesheetParsingStore.attachFilesToRequest) {
    // We have used the the parser to interpret timesheet registrations from the files
    // but we do not want to save the files so we delete them
    await fileDeleter(timesheetParsingStore.uploadedFiles.map((file) => file.localId))
  }
  showSaveModal.value = false
  await saveTimesheet()
  close()
}

const clearParsedTimesheetRegistrations = async (fileDeleter: (ids: string[]) => Promise<any>) => {
  if (timesheetParsingStore.uploadedFiles.length > 0) {
    await fileDeleter(timesheetParsingStore.uploadedFiles.map((file) => file.localId))
    timesheetParsingStore.rollbackParsedTimesheetRegistrations()
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }
}

const handleConfirmDontSave = async (fileDeleter: (ids: string[]) => Promise<any>) => {
  showSaveModal.value = false
  await clearParsedTimesheetRegistrations(fileDeleter)
  close()
}
// Exiting a new timesheet should show warning before leaving
const showCancelModal = ref(false)
const handleConfirmCancel = async (fileDeleter: (ids: string[]) => Promise<any>) => {
  showCancelModal.value = false
  await clearParsedTimesheetRegistrations(fileDeleter)
  close()
}
const handleConfirmDontCancel = () => {
  showCancelModal.value = false
}

const attemptToClose = async () => {
  if (timesheetFormStore.isViewOnly) {
    close()
    return
  }
  if (timesheetFormStore.hasPersistedTimesheet && timesheetFormStore.hasUnsavedChanges) {
    showSaveModal.value = true
    return
  }
  if (!timesheetFormStore.hasPersistedTimesheet && timesheetFormStore.hasUnsavedChanges) {
    showCancelModal.value = true
    return
  }
  close()
}

const showParserAlert = computed(
  () => timesheetParsingStore.countOfValidParsedRegistrationsToBeFilled > 0,
)

const showParserAlertForParsedEntriesOutsideBillingPeriod = computed(
  () =>
    timesheetParsingStore.countOfRegistrationsOutsideOfTheCurrentPeriod > 0 &&
    !timesheetParsingStore.userClosedAlertForParsedEntriesOutsideBillingPeriod,
)

const rollbackAllTimesheetRegistrations = async (fileDeleter: (ids: string[]) => Promise<any>) => {
  await clearParsedTimesheetRegistrations(fileDeleter)
  timesheetFormStore.clearTimesheet()
}
</script>

<style scoped lang="scss">

.modal-timesheet {
  :deep(.wds-modal-content) {
    @apply tw-pb-0;
  }
  :deep(.wds-modal-header) {
    @apply tw-p-0;
  }
  :deep(.wds-modal-footer) {
    @apply tw-px-10;
    @apply tw-pb-8.5;
  }
  :deep(.wds-modal-body) {
    @apply tw-my-0;
    @apply tw-px-10;
    @apply tw-pt-8.5;
    @apply tw-pb-4;
  }
}
</style>
